import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { mapBy } from '@go-shared/lib/functional';
import { CallToActionsInstance } from '@go-shared/types/dailyProgram';
import { getHourFormat } from '@go-shared/lib/property';
import { timeToMinutes } from '@go-shared/lib/time';
import { getBasicTimeStrings, makeEmptyVenueDisposition } from '@go-shared/lib/venue';
import { Audience } from '@go-shared/types/audience';
import { Mcm } from '@go-shared/types/mcm';
import {
  Plan,
  VenueDisplayOptions,
  VenueDisposition,
  VenueHours,
  VenueMealPeriod,
} from '@go-shared/types/plan';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BubbleButton, BubbleInput } from '../BubbleUI';
import { FormGenericContainer } from '../FormGenericContainer';
import VenueForm from '../VenueForm';

interface CanvasVenueFormProps {
  plan: Plan;
  venueTemplates: VenueTemplate[];
  selectedVenueTemplate: VenueTemplate;
  setSelectedVenueTemplate: (nvt: VenueTemplate) => void;
  onUpdateVenueData?: (venueData: VenueDisposition) => Promise<unknown>;
  onClose?: () => void;
}

function getVenueData(plan: Plan, venueTemplate: VenueTemplate) {
  return (
    plan.venueData.find(vd => vd.template === venueTemplate._id) ||
    makeEmptyVenueDisposition(venueTemplate._id, venueTemplate)
  );
}

function CanvasVenueForm(props: CanvasVenueFormProps) {
  const { plan, venueTemplates, selectedVenueTemplate } = props;

  const { activeProperty: property } = useActiveProperty();

  const [hoursSearchVal, setHoursSearchVal] = useState('');

  const venueListRef = useRef<HTMLDivElement>(null);

  const venuesBundle = venueTemplates.map(vt => ({
    id: vt._id,
    name: vt.name,
    template: vt,
    venueData: getVenueData(plan, vt),
  }));
  venuesBundle.sort((a, b) => a.template.order - b.template.order);
  const venuesDict = mapBy(venuesBundle, 'id');

  // const [selectedVenue, setSelectedVenue] = useState<VenueItem>(venues[0]);
  const selectedVenue = venuesDict[selectedVenueTemplate._id];

  const hourFormat = getHourFormat(property);

  async function handleVenueFormSubmit(
    venueHours: VenueHours[],
    mealPeriods: VenueMealPeriod[],
    displayOptions: VenueDisplayOptions,
    callToActions: CallToActionsInstance,
    dynamic?: boolean,
    mcm?: Mcm,
    audience?: Audience
  ) {
    const selectedVenue = venuesDict[selectedVenueTemplate._id];

    if (!selectedVenue) return;

    if (props.onUpdateVenueData) {
      await props.onUpdateVenueData({
        ...selectedVenue.venueData,
        hours: venueHours,
        mealPeriods,
        displayOptions,
        dynamic,
        mcm,
        audience,
        callToActions,
      });
    }
  }

  useEffect(() => {
    setHoursSearchVal('');

    setTimeout(() => {
      venueListRef.current?.querySelectorAll('button').forEach(c => {
        if (!c.classList.contains('inverse')) {
          c.scrollIntoView();
        }
      });
    });
  }, []);

  const vsFuse = useMemo(() => {
    return new Fuse(venuesBundle, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [venueTemplates]);

  const vsFuseSearchResults = useMemo(() => {
    if (!hoursSearchVal || !vsFuse) return undefined;
    return vsFuse.search(hoursSearchVal).map(s => s.item);
  }, [vsFuse, hoursSearchVal]);

  const filteredVenues = vsFuseSearchResults || venuesBundle;
  const dayStartMinutes = timeToMinutes(plan.dayStart);

  return (
    <FormGenericContainer
      className="absolute top-0 left-0 w-full h-full"
      bodyClassName="p-0-i w-full h-full relative bg-eggshell flex flex-col"
    >
      <div className="flex items-start mb-8 pt-6 px-6">
        <div>
          <h1 className="text-lg text-slate-dark font-semibold">Edit Venue</h1>
          <h2 className="text-2xl text-navy">Venue Details</h2>
        </div>
        <BubbleButton
          onClick={props.onClose}
          className="ml-auto"
          variant="navy"
          inverse
          icon="xmark-large"
          iconSize="xl"
        />
      </div>
      <div className="flex items-center sticky top-0 bg-white shadow border-t">
        <BubbleInput
          type="text"
          appearance="bare"
          className="flex-grow shadow-0-i px-3 py-2 bg-white"
          inputClassName="rounded-0-i bg-white"
          placeholder="Search..."
          block
          value={hoursSearchVal}
          onValue={setHoursSearchVal}
        />
        <BubbleButton
          variant="navy"
          inverse
          className="rounded-0-i"
          onClick={() => setHoursSearchVal('')}
          icon="eraser"
        />
      </div>
      <div
        className="flex-grow min-h-0 grid grid-cols-2"
        style={{ gridTemplateColumns: 'min-content auto' }}
      >
        <div
          ref={venueListRef}
          className="h-full overflow-y-auto border-r bg-white"
          style={{ width: 250 }}
        >
          {filteredVenues.map(ven => {
            return (
              <BubbleButton
                key={ven.id}
                inverse={ven.id !== selectedVenueTemplate._id}
                block
                variant="navy"
                className="text-left rounded-0-i"
                onClick={() => props.setSelectedVenueTemplate(ven.template)}
              >
                <div
                  className={`whitespace-normal ${
                    ven.id === selectedVenueTemplate._id ? 'text-white' : 'text-navy'
                  }`}
                  style={{ width: 200 }}
                >
                  {ven.name}
                </div>
                {getBasicTimeStrings(ven.venueData.hours, dayStartMinutes, hourFormat).map(
                  (h, i) => (
                    <div
                      className={`whitespace-nowrap text-xs font-normal ${
                        ven.id === selectedVenueTemplate._id ? 'text-white' : 'text-slate'
                      }`}
                      key={i}
                    >
                      {h}
                    </div>
                  )
                )}
              </BubbleButton>
            );
          })}
          {!filteredVenues.length && (
            <div className="text-center text-slate p-3">No items to show</div>
          )}
        </div>
        <div className="flex-grow h-full overflow-y-auto">
          <VenueForm
            key={selectedVenue.template._id}
            template={selectedVenue.template}
            hourFormat={plan.property.brand.hourFormat}
            propertyType={plan.property.type}
            dayStart={plan.dayStart}
            venueData={selectedVenue.venueData}
            onSubmit={handleVenueFormSubmit}
            mealPeriods={plan.property.brand.mealPeriods}
          />
        </div>
      </div>
    </FormGenericContainer>
  );
}

export default CanvasVenueForm;
